/**
 * useActiveSeason / useIntakeSeason — web client hooks for the
 * season-ssot active-season resolution seam.
 *
 * These are the single sanctioned client-side producer of "the current
 * competition year" (see `.kiro/specs/season-ssot/design.md`,
 * "Season Propagation & Consumer Contract" and "Components and
 * Interfaces → 2. Active-season resolver"). Everything that today reads
 * the `COMPETITION_YEAR` constant becomes a consumer of this seam in a
 * later cutover task — this task only ADDS the hooks (zero behavior
 * change; no existing consumer is repointed here).
 *
 * Contract (identical on web + mobile so both surfaces resolve the same
 * Operative_Year — R3.5, R4.4):
 *   - `useActiveSeason()` wraps the CACHED `getActiveSeason` query and
 *     returns `{ season, operativeYear, status }`.
 *   - `status: 'resolved' | 'unresolved' | 'loading'`.
 *   - `operativeYear` is seeded from the `COMPETITION_YEAR` bootstrap
 *     constant ONLY while `loading` or `unresolved` (the cutover
 *     fallback — R4.2, R13.1). When `resolved`, `operativeYear` is
 *     `season.competitionYear` and NOT the constant.
 *   - `useIntakeSeason(now?)` mirrors the shape but resolves the
 *     intake-window concern. The schema exposes no dedicated
 *     `getIntakeSeason` query, so it derives the result client-side from
 *     `listSeasons` + the pure intake-window predicate against `now`
 *     (single-valued per the concern-scoped resolver invariant —
 *     design Property 8).
 *
 * Auth mode: `getActiveSeason` / `listSeasons` are gated
 * `@aws_cognito_user_pools` ONLY in the schema (unlike the dual-gated
 * `getCountdownAnchor`), so these calls use `authMode: 'userPool'`.
 *
 * Codegen note: `getActiveSeason` / `listSeasons` are NOT present in the
 * web generated client (`@/graphql/queries`) — Task 2 only edited
 * `schema.graphql`, not the generated operations. The query strings are
 * therefore defined locally here rather than imported from codegen.
 *
 * Caching: the design requires a CACHED `getActiveSeason` query (the
 * active season rarely changes). The repo has no shared client cache
 * layer (no react-query; only ad-hoc per-hook TTL caches such as
 * `useRecentActivity`). Following that precedent we use a minimal
 * module-level MEMOIZED-PROMISE cache: the in-flight/resolved query
 * promise is held at module scope so repeated `useActiveSeason()` mounts
 * share ONE network round-trip. `invalidateActiveSeasonCache()` /
 * `invalidateSeasonsCache()` bust it (and `refetch()` calls them).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { COMPETITION_YEAR } from '@/config/competitionYear';

// ---------------------------------------------------------------------------
// Types — mirror the canonical `Season` GraphQL type added in Task 2
// (`infrastructure/graphql/schema.graphql`). Kept structurally identical
// to the mobile hook's local interfaces.
// ---------------------------------------------------------------------------

export type SeasonStatus = 'ACTIVE' | 'SCHEDULED_ARCHIVE' | 'ARCHIVED';

/** A scheduling window (intake / judging). Null bound = open on that side. */
export interface SeasonWindow {
  opensAt: string | null;
  closesAt: string | null;
}

export interface Season {
  competitionYear: number;
  displayName: string;
  status: SeasonStatus;
  intakeWindow: SeasonWindow | null;
  judgingWindow: SeasonWindow | null;
  /**
   * The Phase_2_Due_Date: an absolute ISO-8601 instant after which Phase 2
   * supplemental submission is rejected (season-phase-deadlines spec).
   * Additive and nullable — an absent attribute and `null` both mean "no
   * Phase 2 date restriction". Consumed by the admin + contestant dashboards.
   */
  phaseTwoDueDate?: string | null;
  archiveAt: string | null;
  launchedAt: string;
  archivedAt: string | null;
  freezeError: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Resolution status of a concern-scoped season hook. */
export type SeasonResolutionStatus = 'resolved' | 'unresolved' | 'loading';

/** Shape returned by `useActiveSeason` / `useIntakeSeason`. */
export interface UseActiveSeasonResult {
  /** The resolved Season, or `null` while loading / unresolved. */
  season: Season | null;
  /**
   * The operative competition year. When `resolved` this is
   * `season.competitionYear`; while `loading` or `unresolved` it is the
   * `COMPETITION_YEAR` bootstrap fallback (cutover only).
   */
  operativeYear: number;
  status: SeasonResolutionStatus;
  /** Bust the relevant cache and re-run the query. */
  refetch: () => void;
}

// ---------------------------------------------------------------------------
// GraphQL query strings (defined locally — not in the web codegen client).
// ---------------------------------------------------------------------------

const SEASON_FIELDS = /* GraphQL */ `
  competitionYear
  displayName
  status
  intakeWindow { opensAt closesAt }
  judgingWindow { opensAt closesAt }
  phaseTwoDueDate
  archiveAt
  launchedAt
  archivedAt
  freezeError
  createdAt
  updatedAt
`;

const GET_ACTIVE_SEASON = /* GraphQL */ `
  query GetActiveSeason {
    getActiveSeason {
      ${SEASON_FIELDS}
    }
  }
`;

const LIST_SEASONS = /* GraphQL */ `
  query ListSeasons {
    listSeasons {
      ${SEASON_FIELDS}
    }
  }
`;

interface GetActiveSeasonData {
  getActiveSeason: Season | null;
}

interface ListSeasonsData {
  listSeasons: Season[] | null;
}

// Module-level client — `generateClient()` is a thin handle around the
// configured Amplify API category and is safe to construct once at import
// time (matches `useCountdownAnchor` and the other public-readable hooks).
const client = generateClient();

// ---------------------------------------------------------------------------
// Module-level memoized-promise cache (see file header for rationale).
// ---------------------------------------------------------------------------

let activeSeasonPromise: Promise<Season | null> | null = null;
let seasonsPromise: Promise<Season[]> | null = null;

/**
 * Fetch (or return the cached promise for) the single ACTIVE Season.
 * `getActiveSeason` is non-nullable in the schema and the resolver
 * throws an explicit unresolved error when there is no ACTIVE row; both
 * "null data" and a thrown error are normalized to `null` here so the
 * hook can map them to the `unresolved` state. The cache is busted on
 * rejection so a transient failure can be retried on the next mount.
 */
function fetchActiveSeason(): Promise<Season | null> {
  if (!activeSeasonPromise) {
    activeSeasonPromise = (
      client.graphql({
        query: GET_ACTIVE_SEASON,
        authMode: 'userPool',
      }) as Promise<GraphQLResult<GetActiveSeasonData>>
    )
      .then((res) => res.data?.getActiveSeason ?? null)
      .catch((err) => {
        // Bust the cache so the next mount re-attempts rather than
        // permanently serving a rejected promise.
        activeSeasonPromise = null;
        throw err;
      });
  }
  return activeSeasonPromise;
}

/** Fetch (or return the cached promise for) the full season list. */
function fetchSeasons(): Promise<Season[]> {
  if (!seasonsPromise) {
    seasonsPromise = (
      client.graphql({
        query: LIST_SEASONS,
        authMode: 'userPool',
      }) as Promise<GraphQLResult<ListSeasonsData>>
    )
      .then((res) => res.data?.listSeasons ?? [])
      .catch((err) => {
        seasonsPromise = null;
        throw err;
      });
  }
  return seasonsPromise;
}

/** Bust the cached `getActiveSeason` promise. */
export function invalidateActiveSeasonCache(): void {
  activeSeasonPromise = null;
}

/** Bust the cached `listSeasons` promise. */
export function invalidateSeasonsCache(): void {
  seasonsPromise = null;
}

// ---------------------------------------------------------------------------
// Pure window predicate (shared semantics with backend / mobile).
// ---------------------------------------------------------------------------

/**
 * Pure predicate: is `season`'s intake window open at `now`?
 *
 * An ARCHIVED season is never open. An absent window (`null`) is treated
 * as fully open; a null bound means "open on that side" (R7.2).
 */
export function isIntakeOpen(season: Season, now: Date): boolean {
  if (season.status === 'ARCHIVED') return false;
  const w = season.intakeWindow;
  if (!w) return true;
  const t = now.getTime();
  if (w.opensAt && t < new Date(w.opensAt).getTime()) return false;
  if (w.closesAt && t > new Date(w.closesAt).getTime()) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Resolve the single ACTIVE Season (operative/default reads).
 */
export function useActiveSeason(): UseActiveSeasonResult {
  const [season, setSeason] = useState<Season | null>(null);
  const [status, setStatus] = useState<SeasonResolutionStatus>('loading');

  // Prevent setState after unmount.
  const mountedRef = useRef<boolean>(true);
  // Drop a stale earlier fetch if a newer one supersedes it.
  const fetchIdRef = useRef<number>(0);

  const runFetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    if (mountedRef.current) setStatus('loading');

    try {
      const result = await fetchActiveSeason();
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      if (result) {
        setSeason(result);
        setStatus('resolved');
      } else {
        setSeason(null);
        setStatus('unresolved');
      }
    } catch {
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      // Unresolved (no ACTIVE row / network error) — consumers fall back
      // to the bootstrap constant during cutover.
      setSeason(null);
      setStatus('unresolved');
    }
  }, []);

  const refetch = useCallback(() => {
    invalidateActiveSeasonCache();
    void runFetch();
  }, [runFetch]);

  useEffect(() => {
    mountedRef.current = true;
    void runFetch();
    return () => {
      mountedRef.current = false;
    };
  }, [runFetch]);

  const operativeYear =
    status === 'resolved' && season ? season.competitionYear : COMPETITION_YEAR;

  return { season, operativeYear, status, refetch };
}

/**
 * Resolve the Season whose INTAKE window contains `now` (intake concern).
 *
 * Derived client-side from `listSeasons` since the schema exposes no
 * dedicated `getIntakeSeason` query. Single-valued per the concern
 * invariant: at most one season's intake window is open at any instant.
 *
 * @param now Optional instant to evaluate the intake window against.
 *            Defaults to the current time, snapshotted at fetch time.
 *            Pass an explicit `Date` for deterministic tests.
 */
export function useIntakeSeason(now?: Date): UseActiveSeasonResult {
  const [season, setSeason] = useState<Season | null>(null);
  const [status, setStatus] = useState<SeasonResolutionStatus>('loading');

  const mountedRef = useRef<boolean>(true);
  const fetchIdRef = useRef<number>(0);

  // Snapshot the explicit `now` (if any) into a primitive so the effect
  // dependency is stable. When `now` is undefined we evaluate against
  // `Date.now()` at fetch time without retriggering the effect.
  const nowMs = now ? now.getTime() : undefined;

  const runFetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    if (mountedRef.current) setStatus('loading');

    try {
      const seasons = await fetchSeasons();
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      const at = nowMs !== undefined ? new Date(nowMs) : new Date();
      const open = seasons.find((s) => isIntakeOpen(s, at)) ?? null;
      if (open) {
        setSeason(open);
        setStatus('resolved');
      } else {
        setSeason(null);
        setStatus('unresolved');
      }
    } catch {
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setSeason(null);
      setStatus('unresolved');
    }
  }, [nowMs]);

  const refetch = useCallback(() => {
    invalidateSeasonsCache();
    void runFetch();
  }, [runFetch]);

  useEffect(() => {
    mountedRef.current = true;
    void runFetch();
    return () => {
      mountedRef.current = false;
    };
  }, [runFetch]);

  const operativeYear =
    status === 'resolved' && season ? season.competitionYear : COMPETITION_YEAR;

  return { season, operativeYear, status, refetch };
}

/** Shape returned by `useSeasons`. */
export interface UseSeasonsResult {
  /** Every Season (all statuses), or `[]` while loading / on error. */
  seasons: Season[];
  status: SeasonResolutionStatus;
  /** Bust the season-list cache and re-run the query. */
  refetch: () => void;
}

/**
 * List every Season (cached `listSeasons` query).
 *
 * Backs director surfaces that must offer a Season selection — e.g. the Merit
 * competition-create dialog, which defaults to the active Season and only offers
 * NON-ARCHIVED Seasons (season-ssot R8.3). Callers filter by `status` as needed;
 * this hook returns the full list so the same cache serves every consumer.
 */
export function useSeasons(): UseSeasonsResult {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [status, setStatus] = useState<SeasonResolutionStatus>('loading');

  const mountedRef = useRef<boolean>(true);
  const fetchIdRef = useRef<number>(0);

  const runFetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    if (mountedRef.current) setStatus('loading');

    try {
      const result = await fetchSeasons();
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setSeasons(result);
      setStatus(result.length > 0 ? 'resolved' : 'unresolved');
    } catch {
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setSeasons([]);
      setStatus('unresolved');
    }
  }, []);

  const refetch = useCallback(() => {
    invalidateSeasonsCache();
    void runFetch();
  }, [runFetch]);

  useEffect(() => {
    mountedRef.current = true;
    void runFetch();
    return () => {
      mountedRef.current = false;
    };
  }, [runFetch]);

  return { seasons, status, refetch };
}
